import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AuthProviderApi, AuthState } from './types';
import { oktaAuthProvider } from './oktaAuthProvider';

const AuthContext = createContext<AuthProviderApi | null>(null);

export function AuthRootProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    void oktaAuthProvider.init();
  }, []);

  return <AuthContext.Provider value={oktaAuthProvider}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthProviderApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth requires AuthRootProvider');
  return ctx;
}

export function useAuthState(): AuthState {
  const auth = useAuth();
  return useSyncExternalStore(auth.subscribe, auth.getState, auth.getState);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const state = useAuthState();
  const loc = useLocation();

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</div>
    );
  }
  if (state.status === 'unauthenticated') {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const state = useAuthState();

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</div>
    );
  }
  if (state.status !== 'authenticated' || state.user.role !== 'admin') {
    return <Navigate to="/builders" replace />;
  }
  return <>{children}</>;
}

